/**
 * Quién está atendiendo la conversación: el agente IA o una persona.
 *
 * POR QUÉ EXISTE
 * --------------
 * El portal mostraba el `status` (ACTIVE, WAITING_DOCUMENT, HUMAN_REQUIRED) y
 * el asesor asignado, pero ninguno de los dos responde la pregunta que importa
 * antes de escribir: **si mando esto, ¿el bot va a contestar también?**
 *
 * El 01-set-2026 eso costó un incidente real. Una asesora respondió a un
 * cliente, el cliente contestó 42 minutos después y el bot le mandó el menú por
 * encima. Ella no tenía forma de saber que el bot seguía activo en esa
 * conversación: en pantalla decía "Activa", que es exactamente lo mismo que
 * dice cuando atiende el bot.
 *
 * Quien decide es `conversation_state`, no `status`. Este módulo traduce ese
 * campo —que es interno y no significa nada para una persona— a una frase que
 * dice quién habla y qué pasa con el próximo mensaje del cliente.
 */

export type Atiende = 'ia' | 'asesor' | 'nadie' | 'eligiendo' | 'cerrada'

export interface EstadoAtencion {
  quien: Atiende
  /** Titular corto para el chip. */
  titulo: string
  /** Qué va a pasar con el PRÓXIMO mensaje del cliente. */
  detalle: string
  /** `warn` y `danger` piden acción de alguien. */
  tono: 'brand' | 'purple' | 'warn' | 'danger' | 'muted'
}

/**
 * Estados en los que el bot conduce la conversación.
 *
 * Es el complemento del gate de `_handle_guided_flow`: el bot responde en todo
 * salvo `HUMAN_HANDOFF` y `AWAITING_SLA_CHOICE`. Se listan los estados de bot
 * en vez de los de humano porque los primeros crecen con el flujo (cada paso
 * de carga es uno) y los segundos son dos y estables.
 */
const HUMANO = new Set(['HUMAN_HANDOFF'])
const ELIGIENDO = new Set(['AWAITING_SLA_CHOICE'])

export function estadoAtencion(conv: {
  conversation_state?: string | null
  status?: string | null
  assigned_to?: string | null
  bot_interino?: boolean
}): EstadoAtencion {
  const estado = (conv.conversation_state || '').toUpperCase()
  const status = (conv.status || '').toUpperCase()
  const asesor = (conv.assigned_to || '').split('@')[0]

  if (status === 'CLOSED') {
    return {
      quien: 'cerrada',
      titulo: 'Cerrada',
      // DEF-047: volver a escribir la reabre y le da dueño otra vez.
      detalle: 'Si el cliente vuelve a escribir, se reabre: le contesta el agente IA '
        + 'y vuelve a quien la resolvió, o a otro asesor si esa persona ya no está en la rotación.',
      tono: 'muted',
    }
  }

  if (ELIGIENDO.has(estado)) {
    return {
      quien: 'eligiendo',
      titulo: 'El cliente está decidiendo',
      detalle: 'Se le ofreció que lo atienda el agente IA mientras espera a un asesor.',
      tono: 'warn',
    }
  }

  if (HUMANO.has(estado)) {
    return asesor
      ? {
          quien: 'asesor',
          titulo: `Atiende ${asesor}`,
          detalle: 'El agente IA no responde en esta conversación.',
          tono: 'brand',
        }
      : {
          // Sin asignar y con el bot callado: nadie está atendiendo. Es el
          // agujero que el reloj de SLA vigila, y el que el supervisor tiene
          // que ver en rojo.
          quien: 'nadie',
          titulo: 'Sin asesor asignado',
          detalle: 'El agente IA no responde y nadie tomó la conversación.',
          tono: 'danger',
        }
  }

  // Todo lo demás: el bot conduce.
  return {
    quien: 'ia',
    titulo: 'Responde el agente IA',
    detalle: conv.bot_interino
      ? 'El cliente pidió un asesor y aceptó que lo atienda el agente mientras tanto.'
      : asesor
        ? `Asignada a ${asesor}, pero el agente IA sigue conduciendo: si escribes, tomas la conversación.`
        : 'Si escribes desde el portal, tomas la conversación y el agente deja de responder.',
    tono: conv.bot_interino ? 'warn' : 'purple',
  }
}

/**
 * ¿Se puede devolver al agente IA? (DEF-023)
 *
 * Solo una conversación en manos de una persona: derivada (`HUMAN_HANDOFF`,
 * que es lo que escribe toda derivación) o con el cliente decidiendo si lo
 * atiende el agente mientras espera (`AWAITING_SLA_CHOICE`). Son los dos
 * estados en que corre el reloj del lado humano —`shared/sla_asesor
 * .ESTADOS_CON_RELOJ`, cruzado por test—: con el agente ya conduciendo no hay
 * nada que devolverle, y ofrecer el botón invitaría a un 409.
 *
 * La ventana de 24 h NO entra acá: con la ventana cerrada el botón se ve, pero
 * deshabilitado y con el motivo. Esconderlo haría creer que la acción no
 * existe justo cuando el asesor la está buscando.
 */
export function puedeVolverAlAgente(conv: {
  conversation_state?: string | null
  status?: string | null
}): boolean {
  if ((conv.status || '').toUpperCase() === 'CLOSED') return false
  const estado = (conv.conversation_state || '').toUpperCase()
  return HUMANO.has(estado) || ELIGIENDO.has(estado)
}

/**
 * ¿El asesor le escribió al cliente y el cliente todavía no contestó? (DEF-004)
 *
 * Es la pregunta que hay que hacer antes de «Marcar resuelta». Resolver le
 * devuelve la conversación al bot, así que el próximo mensaje del cliente —la
 * respuesta a ese saludo— lo atiende el agente desde el inicio, con el menú.
 * Logs reales del 01-oct-2026: saludo del asesor a las 20:18:27Z, «Marcar
 * resuelta» 9 s después, y el «Hola» del cliente abrió una sesión nueva: leyó
 * a una persona y enseguida a una máquina que no sabía nada de ella.
 *
 * Se mira el último mensaje del ASESOR, no el último del hilo: un marcador del
 * bot o una nota del sistema después del saludo no son una respuesta del
 * cliente. Lo que cuenta es si el cliente habló después.
 */
export function clienteNoRespondioAlAsesor(
  messages: ReadonlyArray<{ sender: string }>,
): boolean {
  let ultimoDelAsesor = -1
  messages.forEach((m, i) => { if (m.sender === 'operator') ultimoDelAsesor = i })
  if (ultimoDelAsesor < 0) return false
  return !messages.slice(ultimoDelAsesor + 1).some((m) => m.sender === 'user')
}

/**
 * Los estados en que el BOT le hizo una pregunta al cliente y espera su
 * respuesta: la tupla `ESTADOS_ESPERANDO_RESPUESTA` de
 * `conversation/handler.py`, cruzada por test. Hoy son §9.24 («¿qué relación
 * tienes con el titular de la Copia Literal?») y el caso 3.1 de «Casos
 * conversacionales QA» (llegaron Copias Literales de más de un inmueble y el
 * cliente tiene que elegir cuál es el de su solicitud).
 */
const ESPERANDO_RESPUESTA = new Set(['RELACION_TITULAR', 'ELEGIR_INMUEBLE'])

/**
 * Qué se le preguntó, para decírselo al asesor antes de «Marcar resuelta».
 * Cubre los dos conjuntos que ya existían —el cliente eligiendo entre el
 * agente y un asesor (`ELIGIENDO`) y lo que el bot espera
 * (`ESPERANDO_RESPUESTA`)—, y un test exige que no falte ninguno.
 */
const PREGUNTA_ABIERTA: Record<string, string> = {
  AWAITING_SLA_CHOICE: 'si quiere que lo atienda el agente IA mientras espera a un asesor',
  RELACION_TITULAR: 'qué relación tiene con el titular de la Copia Literal',
  ELEGIR_INMUEBLE: 'cuál de los inmuebles de sus Copias Literales es el de su solicitud',
}

/**
 * ¿Hay una pregunta abierta al cliente? (DEF-047, QA de TEST 05-oct-2026)
 *
 * Es la otra pregunta que hay que hacer antes de «Marcar resuelta», al lado de
 * `clienteNoRespondioAlAsesor`. Resolver deja la conversación en `IDLE`, así
 * que la respuesta del cliente —el botón «Atiéndeme tú», la relación con el
 * titular— ya no tiene quién la reciba: el agente lo atiende desde el inicio.
 * QA la resolvió con el estado «El cliente está decidiendo» y nada se lo
 * advirtió.
 *
 * Devuelve qué se le preguntó, en una frase para el asesor, o '' si no hay
 * nada preguntado. Con la conversación ya cerrada no hay nada que advertir.
 */
export function preguntaAbiertaAlCliente(conv: {
  conversation_state?: string | null
  status?: string | null
}): string {
  if ((conv.status || '').toUpperCase() === 'CLOSED') return ''
  const estado = (conv.conversation_state || '').toUpperCase()
  if (!ELIGIENDO.has(estado) && !ESPERANDO_RESPUESTA.has(estado)) return ''
  return PREGUNTA_ABIERTA[estado] ?? 'una pregunta que todavía no contestó'
}

/**
 * ¿La conversación volvió sola y todavía no la atendió una persona? (DEF-047)
 *
 * El cliente escribió sobre una conversación resuelta y la conversación se
 * reabrió (`reabierta_ts`, lo escribe `conversation/_reabrir_si_estaba_resuelta`)
 * asignándosela a quien la resolvió o por reparto. El chip «Reabierta» dice
 * eso en la fila hasta que una persona la atiende.
 *
 * EL CRITERIO ES `asesor_atendio_ts`, el mismo con el que el reloj del lado
 * humano decide que ya hay alguien (DEF-001): lo escriben `manual_reply` y
 * `template_reply` cuando una persona le escribe al cliente. El chip se apaga
 * cuando ese momento es POSTERIOR a la reapertura —uno anterior es de la
 * atención que se resolvió; igual la reapertura lo vacía, por ser una atención
 * nueva—. «Devolver al agente» vacía `asesor_atendio_ts` (DEF-023), así que
 * vacía también `reabierta_ts`: devolverla es haberla atendido. Resuelta otra
 * vez, deja de ser «reabierta» y pasa a «Resueltas».
 */
export function reabiertaSinAtender(conv: {
  status?: string | null
  reabierta_ts?: string | null
  asesor_atendio_ts?: string | null
}): boolean {
  if ((conv.status || '').toUpperCase() === 'CLOSED') return false
  const reabierta = conv.reabierta_ts || ''
  if (!reabierta) return false
  const atendio = conv.asesor_atendio_ts || ''
  return !(atendio && atendio >= reabierta)
}

/**
 * Los estados de §9.30 que cierran el expediente sin nada pendiente. Son los
 * mismos dos que el contrato con el socio proyecta a `COMPLETED`
 * (`teleagent/expediente.A_SESSION_STATUS`), y un test los cruza: con dos
 * listas, el portal y Software dirían cosas distintas del mismo caso.
 *
 * «Con alertas» NO está: ese caso lo tiene que validar una persona.
 */
export const EXPEDIENTE_COMPLETO = new Set([
  'DOCUMENTACION_INICIAL_RECIBIDA',
  'CHECKLIST_INICIAL_COMPLETADO',
])

export function expedienteCompleto(exp?: { estado: string } | null): boolean {
  return Boolean(exp && EXPEDIENTE_COMPLETO.has(exp.estado))
}

/**
 * Los estados de §9.30 en que el expediente espera que el cliente CORRIJA algo:
 * un requisito observado (por el bot o porque el asesor lo rechazó o lo pidió
 * de nuevo). Son los de `teleagent/expediente.py`, cruzados por test.
 *
 * DEF-051 (QA de TEST, 05-oct-2026): tras «Pedir de nuevo» la Ficha y la
 * tarjeta del documento dicen «Pedido de nuevo · esperando reenvío», y la fila
 * de la bandeja decía el `status` crudo —«En revisión», «Activa»—, que no dice
 * nada de eso. Con esto la pill de la bandeja y de la cabecera del chat dice
 * «Documento observado» o «Reenvío solicitado», la etiqueta de §9.30.
 */
export const EXPEDIENTE_OBSERVADO = new Set([
  'DOCUMENTO_OBSERVADO',
  'REENVIO_SOLICITADO',
])

export function expedienteObservado(conv: {
  status?: string | null
  expediente?: { estado: string } | null
}): boolean {
  return Boolean(conv.expediente && EXPEDIENTE_OBSERVADO.has(conv.expediente.estado))
    && (conv.status || '').toUpperCase() !== 'CLOSED'
}

/**
 * «REQUIERE REVISIÓN: ALERTA CRÍTICA» (DEF-039, segunda parte)
 *
 * El expediente se completó con un bloqueo, un embargo o una restricción
 * vigente. Antes quedaba «Documentación recibida con alertas», con el mismo
 * ámbar que una hipoteca que el producto hasta ofrece comprar; ahora es un
 * estado propio (`teleagent/expediente.REVISION_ALERTA_CRITICA`, cruzado por
 * test) y el ejecutivo lo ve primero.
 *
 * No es «completo»: para Software proyecta a IN_REVIEW, igual que «con
 * alertas», así que no está en `EXPEDIENTE_COMPLETO`. Y una conversación
 * resuelta ya no lo pide: la revisó una persona.
 */
export const REVISION_ALERTA_CRITICA = 'REVISION_ALERTA_CRITICA'

export function requiereRevisionCritica(conv: {
  status?: string | null
  expediente?: { estado: string } | null
}): boolean {
  return conv.expediente?.estado === REVISION_ALERTA_CRITICA
    && (conv.status || '').toUpperCase() !== 'CLOSED'
}

/**
 * La lista con las que requieren revisión por una alerta crítica arriba. El
 * resto queda en el orden en que vino: `sort` es estable.
 *
 * Este comentario decía que «una lista que se reordena sola cada vez que llega
 * un mensaje es una lista en la que nadie encuentra nada», y que el orden de
 * llegada era «el más reciente primero». Lo segundo era falso —el índice
 * ordena por creación— y lo primero lo revirtió Rubén el 06-oct-2026: quien
 * escribe sube, como en WhatsApp, porque una lista quieta escondía al cliente
 * que acababa de escribir. Ese orden lo pone `noLeidos.ordenDeLaLista`, que
 * llama a esta función al final para que lo crítico siga primero.
 */
export function primeroLasDeRevision<T extends Parameters<typeof requiereRevisionCritica>[0]>(
  convs: ReadonlyArray<T>,
): T[] {
  return [...convs].sort(
    (a, b) => Number(requiereRevisionCritica(b)) - Number(requiereRevisionCritica(a)))
}

/**
 * COMPLETO POR IA — el agente recibió y validó todo el checklist, sin alertas,
 * y nadie pidió un asesor.
 *
 * Hasta el 28-set-2026 caía en «En curso» con el chip «En revisión», y se leía
 * como una conversación que alguien estaba atendiendo. No es «aprobado»: el
 * bot hace la revisión INICIAL, le dice al cliente que el equipo lo va a
 * revisar, y el destino que fija §9.28 es «Pendiente definición». Pintarlo
 * como aprobado taparía justo lo que falta.
 *
 * Deja de serlo en cuanto una persona toma la conversación: ahí vuelve a
 * «En curso», porque alguien la está atendiendo. Y tampoco lo es la que el
 * cliente reabrió después de resuelta mientras nadie la atienda (DEF-047): el
 * cliente volvió, y eso es trabajo en curso aunque el expediente esté
 * completo — esconderla en «Completos» repetiría a escala chica el defecto.
 */
export function completoPorIA(conv: Parameters<typeof estadoAtencion>[0] & {
  expediente?: { estado: string } | null
  reabierta_ts?: string | null
  asesor_atendio_ts?: string | null
}): boolean {
  return expedienteCompleto(conv.expediente) && estadoAtencion(conv).quien === 'ia'
    && !reabiertaSinAtender(conv)
}

/** Clases del chip por tono, con los tokens del portal. */
export const CLASES_TONO: Record<EstadoAtencion['tono'], string> = {
  brand: 'border-brand-border bg-brand-light text-brand-hover',
  purple: 'border-purple-border bg-purple-light text-purple',
  warn: 'border-warn-border bg-warn-light text-warn',
  danger: 'border-danger-border bg-danger-light text-danger',
  muted: 'border-surface-border bg-surface text-ink-muted',
}
